import React, { useState, useEffect } from "react";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import PinInput from "../../components/PinInput";
import SignupSuccess from "./SignupSuccess";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  verifyEmailDemoAccount,
  verifyOTPDemoAccount,
  fetchAndStoreServerPublicKey,
  createUserLoginMT5Account,
} from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { getPasswordRequirements } from "../../utils/passwordRequirements";

const parseResponseCode = (decryptedData) => {
  let data = decryptedData;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      const codeMatch = data.match(/"CODE"\s*:\s*(\d+)/);
      if (codeMatch) {
        data = { CODE: parseInt(codeMatch[1], 10) };
      } else {
        const codeMatch2 = data.match(/CODE["\s]*[:=]\s*(\d+)/i);
        if (codeMatch2) {
          data = { CODE: parseInt(codeMatch2[1], 10) };
        }
      }
    }
  }

  let responseCode = data?.CODE ?? data?.code;
  if (responseCode == null && data?.body) {
    responseCode = data.body.CODE ?? data.body.code;
  }
  if (responseCode == null && data?.data) {
    responseCode = data.data.CODE ?? data.data.code;
  }
  if (responseCode != null) {
    responseCode = typeof responseCode === "string" ? parseInt(responseCode, 10) : responseCode;
  }
  return { data, responseCode };
};

export default function RegistrationEmail() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState({});
  const [showVerification, setShowVerification] = useState(false);
  const [showPasswordStep, setShowPasswordStep] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [serverKeyReady, setServerKeyReady] = useState(false);

  const [step2Data, setStep2Data] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [userType, setUserType] = useState(null);

  useEffect(() => {
    const savedStep1 = sessionStorage.getItem("registration_step1_data");
    const savedStep2 = sessionStorage.getItem("registration_step2_data");

    if (!savedStep1 || !savedStep2) {
      navigate("/personalInformation", { replace: true });
      return;
    }

    try {
      const step1 = JSON.parse(savedStep1);
      const step2 = JSON.parse(savedStep2);
      setStep2Data(step2);
      if (step1.accountType) setAccountType(step1.accountType);
      if (step1.userType) setUserType(step1.userType);
      if (step2.email) setEmail(step2.email);
    } catch (e) {
      navigate("/personalInformation", { replace: true });
      return;
    }

    const initializeServerKey = async () => {
      try {
        await fetchAndStoreServerPublicKey();
        setServerKeyReady(true);
      } catch (e) {
        setServerKeyReady(false);
        setErrorPopupMessage(
          "Unable to connect to the server. Please refresh the page and try again."
        );
        setShowErrorPopup(true);
      }
    };
    initializeServerKey();
  }, [navigate]);

  const getAccountTypeCode = () => {
    if (!accountType) return 0;
    const value = typeof accountType === "object" ? accountType.value : accountType;
    const map = { mini: 0, standard: 1, premium: 2 };
    return map[value] ?? 0;
  };

  const getUserTypeCode = () => {
    if (!userType) return 0;
    const value = typeof userType === "object" ? userType.value : userType;
    const map = { individual: 0, company: 1, ib: 3 };
    return map[value] ?? 0;
  };

  const getLeverage = () => {
    const savedStep1Data = sessionStorage.getItem("registration_step1_data");
    if (savedStep1Data) {
      try {
        const step1Data = JSON.parse(savedStep1Data);
        if (step1Data.leverage) {
          const leverageValue =
            typeof step1Data.leverage === "object" ? step1Data.leverage.value : step1Data.leverage;
          const match = leverageValue.toString().match(/1:(\d+)/);
          return match ? parseInt(match[1], 10) : 100;
        }
      } catch (e) {
        // ignore
      }
    }
    return 100;
  };

  const getBrokerId = () => {
    const savedStep1Data = sessionStorage.getItem("registration_step1_data");
    if (savedStep1Data) {
      try {
        return JSON.parse(savedStep1Data).brokerId || null;
      } catch (e) {
        // ignore
      }
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00:00.000Z`;
  };

  const getGenderValue = (gender) => {
    if (!gender) return null;
    const genderValue = typeof gender === "object" ? gender.value : gender;
    const map = { male: 0, female: 1 };
    return map[genderValue?.toLowerCase()] ?? null;
  };

  const getMaritalStatusCode = (maritalStatus) => {
    if (!maritalStatus) return null;
    const maritalValue = typeof maritalStatus === "object" ? maritalStatus.value : maritalStatus;
    const map = { single: 0, married: 1, divorced: 2, widowed: 3 };
    return map[maritalValue?.toLowerCase()] ?? null;
  };

  const isMarried = (maritalStatus) => {
    if (!maritalStatus) return false;
    const maritalValue = typeof maritalStatus === "object" ? maritalStatus.value : maritalStatus;
    return maritalValue?.toLowerCase() === "married";
  };

  const validateEmail = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = t("personalInformation.errors.email");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("personalInformation.errors.emailInvalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!password.trim()) {
      newErrors.password = t("personalInformation.password.errors.password");
    } else if (
      password.length < 8 ||
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      newErrors.password = t("personalInformation.password.errors.passwordPolicy");
    }
    if (!passwordConfirmation.trim()) {
      newErrors.passwordConfirmation = t("personalInformation.password.errors.passwordConfirmation");
    } else if (password !== passwordConfirmation) {
      newErrors.passwordConfirmation = t("personalInformation.password.errors.passwordMismatch");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailNext = async () => {
    if (!validateEmail() || !step2Data) return;

    if (!serverKeyReady) {
      try {
        await fetchAndStoreServerPublicKey();
        setServerKeyReady(true);
      } catch (e) {
        setErrorPopupMessage(
          "Unable to connect to the server. Please refresh the page and try again."
        );
        setShowErrorPopup(true);
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const response = await verifyEmailDemoAccount(
        email,
        step2Data.firstName,
        step2Data.lastName,
        getUserTypeCode(),
        step2Data.isAmericanCitizen,
        getAccountTypeCode()
      );

      const { data, responseCode } = parseResponseCode(response.decryptedData);

      if (responseCode === AppConstants.responseOk) {
        setShowVerification(true);
      } else if (responseCode === AppConstants.responseAlreadyOk) {
        setErrorPopupMessage(
          "A verification code was already sent to this email. Please check your inbox."
        );
        setShowErrorPopup(true);
        setShowVerification(true);
      } else if (responseCode === AppConstants.responseFailed) {
        setErrorPopupMessage("Email already exists. Please use a different email address.");
        setShowErrorPopup(true);
      } else if (responseCode === AppConstants.messagesErrorSendingSMS) {
        setErrorPopupMessage(
          data?.MESSAGE ||
            data?.message ||
            "Failed to send verification email. Please check your email address and try again."
        );
        setShowErrorPopup(true);
      } else if (responseCode === AppConstants.catchError) {
        setErrorPopupMessage(
          "The server could not send the verification email right now. This is a temporary server issue — please try again in a few minutes or contact support if it persists."
        );
        setShowErrorPopup(true);
      } else {
        setErrorPopupMessage(
          data?.MESSAGE ||
            data?.message ||
            data?.DATA?.MESSAGE ||
            data?.DATA?.message ||
            "Failed to send verification code. Please try again."
        );
        setShowErrorPopup(true);
      }
    } catch (err) {
      setErrorPopupMessage(err.response?.data?.message || err.message || "An error occurred. Please try again.");
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 5) {
      setError(t("personalInformation.verification.codeRequired"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verifyOTPDemoAccount(email, getAccountTypeCode(), verificationCode);
      const { data, responseCode } = parseResponseCode(response.decryptedData);

      if (responseCode === AppConstants.responseOk) {
        setShowVerification(false);
        setShowPasswordStep(true);
        setVerificationCode("");
      } else {
        setError(data?.MESSAGE || data?.message || "Invalid verification code. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildUserInformation = () => {
    const d = step2Data;
    let phoneValue = null;
    if (d.phoneFullDigits?.trim()) {
      phoneValue = `+${d.phoneFullDigits.trim().replace(/\D/g, "")}`;
    } else if (d.phoneNumber?.trim()) {
      const nationalDigits = d.phoneNumber.trim().replace(/\D/g, "");
      const dial = d.phoneDialCode || d.phoneCountry?.dialCode || null;
      if (dial && nationalDigits) {
        phoneValue = `+${dial}${nationalDigits}`;
      } else if (nationalDigits) {
        phoneValue = `+${nationalDigits}`;
      }
    }

    return {
      EMAIL: email,
      F_NAME: d.firstName,
      L_NAME: d.lastName,
      TYPE: getUserTypeCode(),
      FATHER_NAME: d.fatherName,
      MOTHER_NAME: d.motherName,
      BIRTH_DAY: formatDate(d.dob),
      PLACE_OF_BIRTH: d.placeOfBirth || null,
      MARITAL: getMaritalStatusCode(d.maritalStatus),
      NATIONALITY: null,
      GENDER: getGenderValue(d.gender),
      ADDRESS: null,
      COUNTRY:
        d.phoneCountry && (d.phoneCountry.countryCode || d.phoneCountry.iso2)
          ? (d.phoneCountry.countryCode || d.phoneCountry.iso2).toUpperCase()
          : null,
      CITY: null,
      STATE: null,
      ZIP_CODE: null,
      PHONE: phoneValue,
      INTERNAL_TYPE: getAccountTypeCode(),
      REFERAL_BROKER: getBrokerId() || null,
      DEFAULT_LEVERAGE: getLeverage(),
      SPOUSE_NAME: isMarried(d.maritalStatus) ? d.spouseFirstName : null,
      SPOUSE_PROFESSION: isMarried(d.maritalStatus) ? d.spouseProfession : null,
      PASSWORD: password,
      PASSWORD_CONFIRMATION: passwordConfirmation,
    };
  };

  const handleCreateAccount = async () => {
    if (!validatePassword() || !step2Data) return;

    setLoading(true);
    setError("");

    try {
      const response = await createUserLoginMT5Account(buildUserInformation());
      const { data, responseCode } = parseResponseCode(response.decryptedData);

      if (responseCode === AppConstants.responseOk) {
        localStorage.setItem("userEmail", email);
        localStorage.setItem("registrationEmail", email);

        const brokerId = getBrokerId();
        if (brokerId) {
          localStorage.setItem("registrationBrokerId", brokerId);
        } else {
          localStorage.removeItem("registrationBrokerId");
        }

        localStorage.setItem(
          "registration_step2_data",
          JSON.stringify({ ...step2Data, email })
        );

        sessionStorage.removeItem("registration_step1_data");
        sessionStorage.removeItem("registration_step2_data");
        setShowSuccess(true);
      } else if (responseCode === AppConstants.responseFailed) {
        setErrorPopupMessage(
          data?.MESSAGE ||
            data?.message ||
            "Email already exists. Please use a different email address."
        );
        setShowErrorPopup(true);
      } else if (responseCode === AppConstants.catchError) {
        setErrorPopupMessage(
          data?.MESSAGE ||
            data?.message ||
            "Account creation failed on the server. Please try again."
        );
        setShowErrorPopup(true);
      } else {
        setError(
          data?.MESSAGE ||
            data?.message ||
            data?.error ||
            "Failed to create account. Please try again."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "An error occurred while creating your account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmail = () => {
    setShowVerification(false);
    setShowPasswordStep(false);
    setVerificationCode("");
    setError("");
    setPassword("");
    setPasswordConfirmation("");
    setShowPassword(false);
    setShowPasswordConfirmation(false);
  };

  if (showSuccess) {
    return <SignupSuccess />;
  }

  if (!step2Data) {
    return null;
  }

  const passwordRequirements = getPasswordRequirements(password);
  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  return (
    <div className="page-container">
      <CustomCard className="registration-card registration-card--compact">
        <div className="signup-content">
          <h1 className="signup-title">
            {showPasswordStep
              ? t("personalInformation.password.title")
              : t("personalInformation.emailStep.title")}
          </h1>

          <div className="progress-indicator">
            <div className="progress-circle active">{t("informationAccount.progress.step1")}</div>
            <div className="progress-line progress-line-active" />
            <div className="progress-circle active">
              {t("informationAccount.progress.step2")}
            </div>
            <div className={`progress-line ${showPasswordStep ? "progress-line-active" : ""}`} />
            <div className={`progress-circle ${showPasswordStep ? "active" : ""}`}>
              {t("informationAccount.progress.step3")}
            </div>
          </div>

          <div className="signup-form">
            {!showPasswordStep ? (
              <>
                <p className="email-step-subtitle">
                  {t("personalInformation.emailStep.subtitle")}
                </p>
                <div className="form-group col-12">
                  <label>{t("personalInformation.fields.email")}</label>
                  <div className="input-wrapper">
                    <span className="input-icon">
                      <i className="fa-solid fa-envelope"></i>
                    </span>
                    <input
                      type="email"
                      placeholder={t("personalInformation.placeholders.email")}
                      className={`input-box ${errors.email ? "input-error" : ""}`}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({ ...errors, email: null });
                      }}
                    />
                  </div>
                  {errors.email && <p className="verification-error">{errors.email}</p>}
                </div>

                <div className="button-group-vertical">
                  <CustomButton
                    variant="primary"
                    className="next-button"
                    onClick={handleEmailNext}
                    disabled={loading}
                  >
                    {loading ? t("common.loading") : t("informationAccount.nextButton")}
                  </CustomButton>
                  <CustomButton
                    variant="secondary"
                    className="back-button"
                    onClick={() => navigate("/personalInformation")}
                    disabled={loading}
                  >
                    {t("common.back") || "Back"}
                  </CustomButton>
                </div>
              </>
            ) : (
              <>
                <p className="email-step-subtitle">
                  {t("personalInformation.password.subtitle")}
                </p>
                <div className="form-group col-12">
                  <label>{t("personalInformation.password.fields.password")}</label>
                  <div className="input-wrapper input-wrapper--password">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("personalInformation.password.placeholders.password")}
                      className={`input-box ${errors.password ? "input-error" : ""}`}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors({ ...errors, password: null });
                      }}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="input-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={
                        showPassword
                          ? t("login.hidePassword")
                          : t("login.showPassword")
                      }
                    >
                      <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                  {errors.password && <p className="verification-error">{errors.password}</p>}
                </div>

                <div className="form-group col-12">
                  <label>{t("personalInformation.password.fields.passwordConfirmation")}</label>
                  <div className="input-wrapper input-wrapper--password">
                    <input
                      type={showPasswordConfirmation ? "text" : "password"}
                      placeholder={t("personalInformation.password.placeholders.passwordConfirmation")}
                      className={`input-box ${errors.passwordConfirmation ? "input-error" : ""}`}
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        if (errors.passwordConfirmation) {
                          setErrors({ ...errors, passwordConfirmation: null });
                        }
                      }}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="input-password-toggle"
                      onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                      aria-label={
                        showPasswordConfirmation
                          ? t("login.hidePassword")
                          : t("login.showPassword")
                      }
                    >
                      <i
                        className={`fa-solid ${showPasswordConfirmation ? "fa-eye-slash" : "fa-eye"}`}
                      />
                    </button>
                  </div>
                  {errors.passwordConfirmation && (
                    <p className="verification-error">{errors.passwordConfirmation}</p>
                  )}
                </div>

                <ul className="password-requirements" aria-live="polite">
                  {[
                    { key: "symbol", met: passwordRequirements.symbol },
                    { key: "uppercase", met: passwordRequirements.uppercase },
                    { key: "lowercase", met: passwordRequirements.lowercase },
                    { key: "number", met: passwordRequirements.number },
                    { key: "minLength", met: passwordRequirements.minLength },
                  ].map(({ key, met }) => (
                    <li
                      key={key}
                      className={`password-requirement ${met ? "met" : ""}`}
                    >
                      <span className="password-requirement-box" aria-hidden="true">
                        {met ? <i className="fa-solid fa-check" /> : null}
                      </span>
                      <span>{t(`personalInformation.password.requirements.${key}`)}</span>
                    </li>
                  ))}
                </ul>

                {error && <p className="verification-error">{error}</p>}

                <div className="button-group-vertical">
                  <CustomButton
                    variant="primary"
                    className="next-button"
                    onClick={handleCreateAccount}
                    disabled={loading || !allPasswordRequirementsMet}
                  >
                    {loading ? t("common.loading") : t("personalInformation.password.submitButton")}
                  </CustomButton>
                  <CustomButton
                    variant="secondary"
                    className="back-button"
                    onClick={handleEditEmail}
                    disabled={loading}
                  >
                    {t("common.back") || "Back"}
                  </CustomButton>
                </div>
              </>
            )}
          </div>
        </div>
      </CustomCard>

      {showVerification && (
        <div className="verification-overlay verification-overlay--signup">
          <div className="verification-card verification-card--signup">
            <h1 className="verification-title">{t("personalInformation.verification.title")}</h1>
            <p className="verification-subtitle">{t("personalInformation.verification.subtitle")}</p>

            <PinInput length={5} onChange={setVerificationCode} />

            {error && <p className="verification-error">{error}</p>}

            <button
              className="verification-button"
              onClick={handleVerify}
              disabled={loading || verificationCode.length !== 5}
            >
              {loading ? t("common.loading") : t("personalInformation.verification.verifyButton")}
            </button>

            <button type="button" onClick={handleEditEmail} className="verification-edit-link">
              {t("personalInformation.verification.editEmail")}
            </button>
          </div>
        </div>
      )}

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title="Error"
        message={errorPopupMessage}
        buttonText="OK"
        icon="fa-circle-exclamation"
      />
    </div>
  );
}
